// ============================================
// Tonemap Post-Process Effect
// ============================================
// HDR → LDR tone mapping. Place LAST in the chain (after bloom/grading) — the
// scene renders into an rgba16float target, and without a tonemapper anything
// above 1.0 just hard-clips at the LDR backbuffer.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { TONEMAP_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Tone mapping operator
 */
export enum TonemapMode {
  Reinhard = 0,          // Soft, never clips
  ReinhardExtended = 1,  // Reinhard with a configurable white point
  ACES = 2,              // Filmic (Hill fit) — industry default
  Uncharted2 = 3,        // Hable filmic with a pronounced toe
  AgX = 4,               // Modern, hue-preserving under heavy exposure
  PBRNeutral = 5,        // Khronos color-accurate operator
}

/**
 * Tonemap post-processing effect
 *
 * Compresses the HDR scene into displayable range using a selectable film curve.
 * `exposure` is in EV (stops); `whitePoint` only affects ReinhardExtended / Uncharted2.
 */
export class TonemapEffect extends PostProcessEffect {
  readonly type = 'tonemap';
  readonly displayName = 'Tonemap';

  // Parameters
  exposure = 0.0;
  mode = TonemapMode.ACES;
  whitePoint = 11.2;
  /** Blue-noise-style output dithering (IGN) on the LDR write — kills 8-bit banding. */
  dither = true;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = TONEMAP_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'mode', type: 'int', min: 0, max: 5, default: 2, description: 'Operator (0=Reinhard, 1=Reinhard-W, 2=ACES, 3=Uncharted2, 4=AgX, 5=PBR Neutral)' },
      { name: 'exposure', type: 'float', min: -5, max: 5, default: 0.0, description: 'Exposure (EV)' },
      { name: 'whitePoint', type: 'float', min: 1, max: 20, default: 11.2, description: 'White point (Reinhard-W / Uncharted2)' },
      { name: 'dither', type: 'bool', default: true, description: 'Dither the LDR output (IGN) to kill 8-bit banding' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'mode': return this.mode;
      case 'exposure': return this.exposure;
      case 'whitePoint': return this.whitePoint;
      case 'dither': return this.dither;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (name === 'dither') {
      this.dither = value === true || value === 1;
      return;
    }
    if (typeof value !== 'number') return;
    switch (name) {
      case 'mode': this.mode = Math.floor(Math.max(0, Math.min(5, value))) as TonemapMode; break;
      case 'exposure': this.exposure = Math.max(-10, Math.min(10, value)); break;
      case 'whitePoint': this.whitePoint = Math.max(1, value); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const tonemapWgsl = getCompiledShader('postprocess/tonemap');
    this.shader = ctx.createShader(tonemapWgsl, tonemapWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'tonemap-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'tonemap-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      // Effect intensity fades the exposure toward neutral — a cheap way to animate the
      // tonemapper in/out without a branch in the shader.
      exposure: this.exposure * intensity,
      mode: this.mode,
      whitePoint: this.whitePoint,
      // Dither only matters on the 8-bit write; harmless (and skipped) on HDR intermediates.
      ditherStrength: this.dither && !isHdrTarget ? 1 / 255 : 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
